import m from 'mithril';
import { Store } from '../store';
import { ApiService } from '../services/api';
import { currentLang } from '../i18n';

let isOpen = false;

export const UserProfileMenu: m.Component = {
    oncreate({ dom }) {
        const handler = (e: MouseEvent | TouchEvent) => {
            if (!dom.contains(e.target as Node)) { isOpen = false; m.redraw(); }
        };
        document.addEventListener('click', handler);
        document.addEventListener('touchstart', handler, { passive: true });
        (dom as any)._cleanup = () => {
            document.removeEventListener('click', handler);
            document.removeEventListener('touchstart', handler);
        };
    },
    onremove({ dom }) { (dom as any)._cleanup?.(); },
    view() {
        const user = Store.state.user;
        if (!user) return null;
        const initials = (user.name || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

        const menuItems = [
            { icon: 'lucide:user',         label: 'My Profile', tab: 'profile',  color: '#3b82f6' },
            { icon: 'lucide:shield-check', label: 'Security',   tab: 'security', color: '#22c55e' },
            { icon: 'lucide:package',      label: 'My Orders',  tab: 'orders',   color: '#f97316', divider: true },
            { icon: 'lucide:heart',        label: 'Wishlist',   tab: null as string | null, color: '#ec4899' },
        ];

        return m('div', { class: 'relative block' }, [
            // ── Trigger ──────────────────────────────────────────────────────
            m('button', {
                class: `!m-0 !border-0 flex items-center gap-2 px-2 py-1 rounded-full transition-all duration-200 cursor-pointer
                        ${isOpen
                            ? 'ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-surface)] shadow-md'
                            : 'hover:bg-[var(--color-border)]/60'}`,
                onclick: (e: Event) => { e.stopPropagation(); isOpen = !isOpen; m.redraw(); }
            }, [
                m('div', {
                    class: 'w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-black shadow-sm',
                    style: 'background: linear-gradient(135deg, var(--color-primary), var(--color-secondary))'
                }, initials),
                m('span', { class: 'hidden sm:block font-bold text-sm text-[var(--color-text-base)] opacity-80 max-w-[100px] truncate' },
                    user.name.split(' ')[0]
                ),
                m('iconify-icon', { icon: isOpen ? 'lucide:chevron-up' : 'lucide:chevron-down', class: 'text-xs opacity-40' })
            ]),

            // ── Dropdown ─────────────────────────────────────────────────────
            isOpen && m('div', {
                class: `absolute end-0 ${currentLang === 'ar' ? 'origin-top-left' : 'origin-top-right'} top-[calc(100%+10px)] z-[100] w-[260px] animate-scale-in`,
                onclick: (e: Event) => e.stopPropagation()
            }, [
                m('div', { class: 'bg-[var(--color-surface)] rounded-2xl shadow-xl border border-[var(--color-border)] overflow-hidden' }, [

                    // Header with gradient tint
                    m('div', {
                        class: 'p-3.5 border-b border-[var(--color-border)] flex items-center gap-3',
                        style: 'background: linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 10%, transparent), transparent)'
                    }, [
                        m('div', {
                            class: 'w-10 h-10 rounded-xl text-white flex items-center justify-center text-sm font-black shadow-sm shrink-0',
                            style: 'background: linear-gradient(135deg, var(--color-primary), var(--color-secondary))'
                        }, initials),
                        m('div', { class: 'flex-1 min-w-0' }, [
                            m('p', { class: 'font-black text-sm truncate text-[var(--color-text-base)]' }, user.name),
                            m('p', { class: 'text-[11px] opacity-50 truncate mt-0.5' }, user.email)
                        ]),
                        // X close button
                        m('button', {
                            class: '!m-0 !p-1.5 !border-0 !bg-transparent rounded-lg opacity-40 hover:opacity-100 hover:!bg-[var(--color-border)] transition-all cursor-pointer shrink-0',
                            onclick: (e: Event) => { e.stopPropagation(); isOpen = false; m.redraw(); }
                        }, m('iconify-icon', { icon: 'lucide:x', class: 'text-base' }))
                    ]),

                    // Admin Portal link removed as per user request

                    // Nav items with colored icons
                    m('div', { class: 'p-2 flex flex-col gap-0.5' },
                        menuItems.map((item, i) =>
                            m('button', {
                                key: i,
                                class: [
                                    '!m-0 !border-0 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 text-left hover:bg-[var(--color-border)]/60',
                                    item.divider ? 'border-b border-[var(--color-border)] pb-3 mb-1 !rounded-b-none' : ''
                                ].join(' '),
                                onclick: () => {
                                    isOpen = false;
                                    m.redraw();
                                    if (item.tab) m.route.set('/profile', { tab: item.tab });
                                    else Store.addToast(`${item.label} coming soon!`);
                                }
                            }, [
                                m('div', {
                                    class: 'w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-base transition-all duration-200',
                                    style: `background: ${item.color}20; color: ${item.color}`
                                }, [m('iconify-icon', { icon: item.icon })]),
                                m('span', { class: 'font-semibold text-sm text-[var(--color-text-base)]' }, item.label)
                            ])
                        )
                    ),

                    // Sign Out footer
                    m('div', { class: 'p-2 border-t border-[var(--color-border)]' }, [
                        m('button', {
                            class: '!m-0 !border-0 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all text-left hover:bg-red-500/10 group',
                            onclick: async () => {
                                await ApiService.logout();
                                Store.state.user = null;
                                isOpen = false;
                                m.redraw();
                                Store.addToast('Signed out.', 'success');
                            }
                        }, [
                            m('div', {
                                class: 'w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0 group-hover:bg-red-500 transition-colors duration-200'
                            }, [
                                m('iconify-icon', { icon: 'lucide:log-out', class: 'text-red-500 group-hover:text-white transition-colors duration-200' })
                            ]),
                            m('span', { class: 'font-bold text-sm text-red-500' }, 'Sign Out')
                        ])
                    ])
                ])
            ])
        ]);
    }
};
